import React, { useRef, useState } from 'react';
import { Input } from '@/shared/components/ui/Input';
import { Tooltip } from '@/shared/components/ui/Tooltip';
import { Icon } from '@/shared/icons';
import { api } from '@/lib/api';

export interface UploadInputProps {
  value: string;
  onChange: (url: string) => void;
  placeholder?: string;
  accept?: string;
  label?: string;
  required?: boolean;
  /** Pasta de destino no upload. Default: 'uploads' */
  folder?: string;
  /**
   * Prefixo dos `data-testid` internos. Default `'upload-input'`, que preserva
   * os ids historicos (`upload-input-button` / `upload-input-file`). Passe um
   * prefixo proprio quando a tela tiver mais de um upload.
   */
  testId?: string;
}

export const UploadInput: React.FC<UploadInputProps> = ({
  value,
  onChange,
  placeholder = 'https://...',
  accept,
  label,
  required,
  folder = 'uploads',
  testId = 'upload-input',
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<{ success: boolean; data: { url: string } }>(
        `/uploads?folder=${folder}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      onChange(response.data.data.url);
    } catch (error) {
      console.error('[UploadInput] Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {label && (
        <label className="block text-xs font-medium mb-1.5" style={{ color: '#475569' }}>
          {label} {required && <span className="text-red-500 text-[10px]">*</span>}
        </label>
      )}
      <div className="flex gap-1.5">
        <div className="flex-1">
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={`${testId}-url`}
          />
        </div>
        <Tooltip content="Fazer upload de arquivo">
          <button
            type="button"
            className="flex items-center justify-center w-9 h-9 rounded-lg border transition-colors"
            style={{
              backgroundColor: 'var(--theme-input-background)',
              borderColor: 'var(--theme-input-border)',
              color: uploading ? '#8b5cf6' : '#64748b',
            }}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid={`${testId}-button`}
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-500 border-t-transparent" />
            ) : (
              <Icon name="upload" className="w-4 h-4" />
            )}
          </button>
        </Tooltip>
        <input
          ref={fileRef}
          type="file"
          className="hidden"
          accept={accept}
          data-testid={`${testId}-file`}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleUpload(file);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
};

export default UploadInput;
